import { motion } from "framer-motion";
import { Building2, Clock, Mail, MapPin, Phone, Printer, Siren } from "lucide-react";
import { CONTACT } from "../data/content";

export const Footer = () => (
  <footer id="iletisim" data-testid="contact-section" className="bg-[#16281D] text-[#F8F7F4] pt-24 md:pt-32 pb-10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="text-xs uppercase tracking-[0.3em] font-bold text-[#D4A373] mb-6"
      >
        İletişim
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
        className="font-serif font-light text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[1.05] max-w-4xl"
      >
        Kapımız her zaman <em className="italic text-[#D4A373]">ailenize</em> açık.
      </motion.h2>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-16 md:mt-24">
        <div className="lg:col-span-7 space-y-10">
          <div className="flex items-start gap-5">
            <span className="w-11 h-11 rounded-full bg-[#F8F7F4]/10 flex items-center justify-center shrink-0">
              <Phone aria-hidden="true" size={18} strokeWidth={1.5} />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#F8F7F4]/50 mb-2">Telefon</p>
              <p data-testid="contact-phone" className="font-serif text-2xl md:text-4xl">{CONTACT.phone}</p>
            </div>
          </div>
          <div className="flex items-start gap-5">
            <span className="w-11 h-11 rounded-full bg-[#F8F7F4]/10 flex items-center justify-center shrink-0">
              <MapPin aria-hidden="true" size={18} strokeWidth={1.5} />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#F8F7F4]/50 mb-2">Adres</p>
              {CONTACT.address.map((line) => (
                <p key={line} data-testid="contact-address" className="font-serif text-xl md:text-2xl text-[#F8F7F4]/90">{line}</p>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4 bg-[#B85C38]/15 border border-[#B85C38]/30 rounded-xl px-6 py-4 max-w-md">
            <Siren aria-hidden="true" size={20} className="text-[#D4A373] shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#F8F7F4]/80">{CONTACT.emergency}</p>
          </div>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="border border-[#F8F7F4]/15 rounded-2xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-8">
              <Clock aria-hidden="true" size={18} strokeWidth={1.5} className="text-[#D4A373]" />
              <p className="text-xs uppercase tracking-[0.2em] font-bold">Çalışma Saatleri</p>
            </div>
            {CONTACT.hours.map((h, i) => (
              <div
                key={h.day}
                data-testid={`working-hours-${i}`}
                className={`flex justify-between py-4 ${i !== 0 ? "border-t border-[#F8F7F4]/10" : ""}`}
              >
                <span className="text-[#F8F7F4]/60">{h.day}</span>
                <span className="font-serif text-lg">{h.time}</span>
              </div>
            ))}
          </div>
          <div className="overflow-hidden rounded-2xl border border-[#F8F7F4]/15">
            <iframe
              title="Taşpınar ASM Konum"
              data-testid="contact-map"
              src={CONTACT.mapEmbed}
              className="w-full h-[280px] grayscale-[40%] contrast-[0.95]"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>

      <motion.section
        aria-labelledby="provincial-directorate-title"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="mt-16 md:mt-24 pt-12 border-t border-[#F8F7F4]/10"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4">
            <span className="w-11 h-11 rounded-full bg-[#D4A373]/15 text-[#D4A373] flex items-center justify-center mb-6">
              <Building2 aria-hidden="true" size={20} strokeWidth={1.5} />
            </span>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4A373] font-bold mb-3">
              Bağlı Kurum
            </p>
            <h3
              id="provincial-directorate-title"
              className="font-serif text-3xl md:text-4xl leading-tight max-w-md"
            >
              {CONTACT.provincialDirectorate.name}
            </h3>
          </div>

          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
            <div className="flex items-start gap-4">
              <Phone aria-hidden="true" size={18} strokeWidth={1.5} className="text-[#D4A373] mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[#F8F7F4]/50 mb-2">Telefon</p>
                <div className="flex flex-wrap gap-x-2 gap-y-1 font-serif text-lg">
                  {CONTACT.provincialDirectorate.phones.map((phone, index) => (
                    <span key={phone.href} className="inline-flex items-center gap-2">
                      <a
                        href={phone.href}
                        className="underline-offset-4 hover:text-[#D4A373] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373] rounded-sm transition-colors"
                      >
                        {phone.label}
                      </a>
                      {index < CONTACT.provincialDirectorate.phones.length - 1 && (
                        <span aria-hidden="true" className="text-[#F8F7F4]/30">/</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Printer aria-hidden="true" size={18} strokeWidth={1.5} className="text-[#D4A373] mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[#F8F7F4]/50 mb-2">Faks</p>
                <p className="font-serif text-lg">{CONTACT.provincialDirectorate.fax}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 min-w-0">
              <Mail aria-hidden="true" size={18} strokeWidth={1.5} className="text-[#D4A373] mt-1 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-[#F8F7F4]/50 mb-2">E-posta</p>
                <a
                  href={`mailto:${CONTACT.provincialDirectorate.email}`}
                  className="font-serif text-lg break-all underline-offset-4 hover:text-[#D4A373] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373] rounded-sm transition-colors"
                >
                  {CONTACT.provincialDirectorate.email}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4 min-w-0">
              <Mail aria-hidden="true" size={18} strokeWidth={1.5} className="text-[#D4A373] mt-1 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-[#F8F7F4]/50 mb-2">KEP</p>
                <p className="font-serif text-lg break-all">{CONTACT.provincialDirectorate.kep}</p>
              </div>
            </div>

            <div className="sm:col-span-2 flex items-start gap-4 pt-1">
              <MapPin aria-hidden="true" size={18} strokeWidth={1.5} className="text-[#D4A373] mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[#F8F7F4]/50 mb-2">Adres</p>
                <a
                  href={CONTACT.provincialDirectorate.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-serif text-lg md:text-xl leading-relaxed underline-offset-4 hover:text-[#D4A373] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373] rounded-sm transition-colors"
                >
                  {CONTACT.provincialDirectorate.address}
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      <div className="mt-20 md:mt-28 pt-8 border-t border-[#F8F7F4]/10 flex flex-col md:flex-row justify-between gap-4 text-sm text-[#F8F7F4]/40">
        <p>© 2026 Aksaray Merkez Taşpınar Aile Sağlığı Merkezi. Tüm hakları saklıdır.</p>
        <p>Sağlığınız, ailemizin merkezinde.</p>
      </div>
    </div>
  </footer>
);
